import { fallback, http, type Transport } from 'viem'
import { environment } from 'src/environments/environment'

/**
 * Normalises a chain id to the decimal string key used by environment.
 * Callers pass hex ('0x13882'), decimal strings ('80002') or numbers.
 * @param {string | number} chainId
 * @returns {string}
 */
export function normaliseChainId(chainId: string | number): string {
    const value = String(chainId)

    return value.startsWith('0x') ? parseInt(value, 16).toString() : value
}

/**
 * Resolves the ordered RPC endpoint list for a chain. Mirrors the dApp:
 * PROVIDERS is the full ordered list, PROVIDER is the legacy single value.
 * @param {string | number} chainId defaults to the first supported chain
 * @returns {string[]}
 */
export function rpcUrls(chainId: string | number = environment.SUPPORTED_CHAINS[0]): string[] {
    const chain = (environment as any)[normaliseChainId(chainId)]
    const urls: string[] = chain?.PROVIDERS?.length ? chain.PROVIDERS : [chain?.PROVIDER]

    return urls.filter(Boolean)
}

/**
 * Builds a viem transport that walks down the endpoint list when one
 * errors out or times out.
 * @param {string | number} chainId defaults to the first supported chain
 * @returns {Transport}
 */
export function rpcTransport(chainId: string | number = environment.SUPPORTED_CHAINS[0]): Transport {
    const urls = rpcUrls(chainId)

    if (!urls.length) {
        throw new Error(`No RPC endpoint configured for chain ${chainId}. Set CHAIN_${normaliseChainId(chainId)}__PROVIDERS or CHAIN_${normaliseChainId(chainId)}__PROVIDER.`)
    }

    return fallback(urls.map((url) => http(url)), { retryCount: 1 })
}
